import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Divider, Paper, Stack } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  approveRecordsBulk,
  deleteFile,
  deleteRecordsBulk,
  fetchAllRecordIds,
  fetchRecord,
  resolveFileDuplicates,
  toProblemDetail,
  updateRecord
} from '../../../services/syntiro_honey/data_ingestion.service';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import {
  BulkSkippedRecord,
  EsgRecord,
  EsgRecordUpdateResponse,
  FileDuplicateResolution,
  ProblemDetail,
  RecordListQuery,
  ResolveDuplicatesResult,
  UpdateRecordPatch
} from '../../../types/syntiro_honey';
import type { ReviewFileSeed } from '../../../types/syntiro_honey';
import { bulkApprovalLearningFeedback } from './alias_feedback';
import BulkActionBar from './BulkActionBar';
import BulkEditDialog from './BulkEditDialog';
import CompletenessMeter from './CompletenessMeter';
import ConfirmationModal from '../../shared/modal/ConfirmationModal';
import FileDuplicateBanner from './FileDuplicateBanner';
import FileDuplicateChoiceDialog from './FileDuplicateChoiceDialog';
import RecordDetailModal from './RecordDetailModal';
import RecordGroupList from './RecordGroupList';
import RecordsGrid from './RecordsGrid';
import ReviewFilterBar, { GroupByOption } from './ReviewFilterBar';
import ReviewScopeTabs, { ReviewScope, filtersForScope, scopeFromFilters } from './ReviewScopeTabs';
import ReviewSummaryChips from './ReviewSummaryChips';
import SiblingConfirmBar from './SiblingConfirmBar';
import { findSiblingsToConfirm, SiblingConfirmTarget } from './sibling_confirm';
import { toSortQuery } from './record_sort';
import { ReviewFilters, useRecordList } from './useRecordList';
import { useCompleteness } from './useCompleteness';
import { useFileDuplicateSummary } from './useFileDuplicateSummary';
import { useRecordStats } from './useRecordStats';

/** Outcome of a bulk action, rendered as a dismissible summary alert. */
interface BulkOutcome {
  /** Past-tense verb for the summary line. */
  verb: 'approved' | 'deleted' | 'confirmed' | 'edited';
  /** Number of records the action succeeded for. */
  doneCount: number;
  /** Records the backend skipped, with per-id reasons. */
  skipped: Array<BulkSkippedRecord>;
}

/** The backend's reason string for a record that was already in the target state. */
const SKIP_REASON_ALREADY_APPROVED = 'already approved';

/** Reassures the reviewer that an all-already-approved skip list needed no action. */
function describeAlreadyApproved(count: number): string {
  return count === 1
    ? 'One was already approved, so it needed no change.'
    : `${count} were already approved, so they needed no change.`;
}

/**
 * Names a skipped record for the reviewer: the label the backend returned, else one
 * from the records already on screen, and only then the id as a last resort.
 */
function nameSkippedRecord(skipped: BulkSkippedRecord, labels: Map<string, string>): string {
  return skipped.rawLabel ?? labels.get(skipped.id) ?? skipped.id;
}

/**
 * Best-effort label for a client-built skip entry, resolved once at push time
 * rather than left to the render-time `recordLabels` fallback — which is
 * memoised on the current page and gets rebuilt by the very refetch that
 * follows a bulk action, so a skip outcome could show a real label for a
 * moment and then flip to a bare uuid once that refetch lands. Also the only
 * way an off-page selection's label is ever known at all, since `recordLabels`
 * never covers rows outside the current page.
 */
async function skipLabel(
  recordId: string,
  onPage: Map<string, EsgRecord>
): Promise<string | undefined> {
  const known = onPage.get(recordId)?.rawLabel;
  if (known !== undefined) {
    return known;
  }
  try {
    return (await fetchRecord(recordId)).rawLabel;
  } catch {
    return undefined;
  }
}

/**
 * One human sentence for what a duplicate resolution actually did, fed to the
 * success toast — the reviewer must see the record-level consequences, not just
 * "resolved" (Maxine 2026-08-19).
 */
function describeResolutionOutcome(result: ResolveDuplicatesResult): string {
  if (result.resolution === 'PROCESS_AS_NEW') {
    return 'All flagged records were kept as new records.';
  }
  const parts: Array<string> = [];
  if (result.superseded > 0) {
    parts.push(`${result.superseded} earlier value${result.superseded === 1 ? '' : 's'} updated`);
  }
  if (result.removedDuplicates > 0) {
    parts.push(
      `${result.removedDuplicates} duplicate${result.removedDuplicates === 1 ? '' : 's'} removed`
    );
  }
  if (result.keptNew > 0) {
    parts.push(`${result.keptNew} kept as new`);
  }
  if (result.skippedApproved > 0) {
    parts.push(
      `${result.skippedApproved} approved record${result.skippedApproved === 1 ? '' : 's'} left untouched`
    );
  }
  return parts.length > 0 ? `Duplicates resolved: ${parts.join(', ')}.` : 'Duplicates resolved.';
}

interface ReviewPanelProps {
  /** A file handed over from the upload step: opens the list filtered to it. */
  seed?: ReviewFileSeed | null;
  /**
   * Called when the reviewer dismisses the seeded file filter. The parent owns the
   * seed, so only clearing it there makes the dismissal survive a tab switch (the
   * panel unmounts per tab and would otherwise re-apply the seed on remount).
   */
  onSeedDismissed?: () => void;
  /**
   * Viewer mode (DECISIONS #39): render the data without any mutation surface —
   * no selection/bulk actions, no confirm buttons, detail modal is view-only.
   */
  readOnly?: boolean;
}

/**
 * Review step of the Syntiro Honey Data Ingestion view: server-paginated
 * record list with confidence bands, filtering, a detail drawer for
 * edit/map/mark actions, and single + bulk approve/delete. When the upload
 * step hands a processed file over, the list opens filtered to that file,
 * with a dismissible chip naming it.
 * @param {ReviewFileSeed | null | undefined} seed The upload → review handoff, if any.
 * @returns {JSX.Element}
 */
export default function ReviewPanel({
  seed = null,
  onSeedDismissed,
  readOnly = false
}: ReviewPanelProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [filters, setFilters] = useState<ReviewFilters>({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  // Default to grouping by metric so the reviewer faces metric-level headers, not 300 rows.
  const [groupBy, setGroupBy] = useState<GroupByOption>('metric');
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  // The record ids the open modal steps through with Prev/Next (the flat page, or
  // the loaded rows of the group the record was opened from).
  const [siblingIds, setSiblingIds] = useState<Array<string>>([]);
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [bulkOutcome, setBulkOutcome] = useState<BulkOutcome | null>(null);
  const [bulkProblem, setBulkProblem] = useState<ProblemDetail | null>(null);
  // The pending "apply this confirmation to the rows sharing its label" offer, or
  // null when the last confirm had no qualifying siblings (the common case).
  const [siblingTarget, setSiblingTarget] = useState<SiblingConfirmTarget | null>(null);
  // Whether the bulk-edit dialog (Maxine R5 ②) is open for the current selection.
  const [bulkEditOpen, setBulkEditOpen] = useState<boolean>(false);
  // The file-level duplicate decision (Maxine 2026-08-19 S2): the 4-way dialog and
  // the "delete this file" confirm, both scoped to the active file filter.
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
  const [resolvingDuplicates, setResolvingDuplicates] = useState<boolean>(false);
  const [deleteFileConfirmOpen, setDeleteFileConfirmOpen] = useState<boolean>(false);
  // Captured at the moment the confirmation opens, not read live from
  // `completeness` while the modal is showing — a background refetch (it resets
  // to null at the start of every run) must not blank out the count on a
  // destructive, irreversible confirmation the reviewer is actively looking at.
  const [deleteConfirmRecordCount, setDeleteConfirmRecordCount] = useState<number | null>(null);
  const [deletingFile, setDeletingFile] = useState<boolean>(false);

  // KNOWN LIMITATION: offset paging under the "Needs action" scope — approving a
  // record removes it from the filtered set, shifting later records back across
  // page boundaries, so a reviewer walking forward page-by-page can miss rows that
  // slid into pages already visited. Needs keyset/cursor paging (or holding the
  // offset, not the page index, across mutation refetches) as a follow-up.
  const query = useMemo<RecordListQuery>(
    () => ({
      ...filters,
      ...toSortQuery(sortModel),
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }),
    [filters, sortModel, paginationModel]
  );
  const { result, loading, problem, refresh } = useRecordList(query);
  // Fallback labels for a bulk outcome, from the rows already on screen. The API
  // names skipped records itself; this only covers a frontend running ahead of it.
  const recordLabels = useMemo(
    () => new Map((result?.items ?? []).map((item) => [item.id, item.rawLabel])),
    [result]
  );
  // The stats are unfiltered totals, so they only re-fetch on mutations and
  // manual refreshes (not on every filter/page change).
  const [statsVersion, setStatsVersion] = useState<number>(0);
  const stats = useRecordStats(statsVersion);
  // Completeness is scoped to the file being reviewed (or the whole backlog when
  // no file is selected) — the "how done is this workbook" score (Maxine).
  const completeness = useCompleteness(filters.fileId, statsVersion);
  // The scoped file's duplicate state: drives the resolution banner. Viewers
  // cannot decide, so the fetch is skipped entirely in read-only mode.
  const duplicateSummary = useFileDuplicateSummary(
    readOnly ? undefined : filters.fileId,
    statsVersion
  );

  const refreshAll = async (): Promise<void> => {
    await refresh();
    // A mutation may have made selected rows non-existent or non-selectable
    // (e.g. approved via the detail modal): drop the selection rather than let
    // the bulk bar act on rows the reviewer can no longer see as checked.
    setSelection([]);
    setStatsVersion((version) => version + 1);
  };

  const handleFiltersChange = (next: ReviewFilters): void => {
    // Removing the file filter — from ANY control (header chip, filter chips,
    // "Clear all", the panel's "All files") — must also clear the seed at its
    // owner, or a tab switch (which remounts this panel) would silently
    // re-apply the dismissed filter.
    if (filters.fileId !== undefined && next.fileId === undefined) {
      onSeedDismissed?.();
    }
    setFilters(next);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    // The visible set changed — a carried-over selection would be invisible
    // (keepNonExistentRowsSelected) yet still drive the bulk bar.
    setSelection([]);
    // Likewise the sibling offer: it was computed for the previous view, and
    // applying it here would edit rows the reviewer has navigated away from.
    setSiblingTarget(null);
  };

  /** Coarse scope (All / Needs action / Approved) → the status/excludeApproved filters. */
  const handleScopeChange = (scope: ReviewScope): void => {
    if (scope === scopeFromFilters(filters)) {
      return; // clicking the already-active tab must not refetch or drop selection
    }
    handleFiltersChange({ ...filters, ...filtersForScope(scope) });
  };

  // Apply the upload → review handoff whenever a (new) file is handed over: filter
  // the list to that file's records so the reviewer continues where the upload
  // finished, instead of hunting its rows in the full backlog. The functional
  // updates return the SAME reference when the seed is already applied, so a
  // remount with an unchanged seed does not re-render or refetch.
  useEffect(() => {
    if (seed !== null) {
      setFilters((current) =>
        current.fileId === seed.fileId ? current : { ...current, fileId: seed.fileId }
      );
      setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    }
  }, [seed]);

  const handleSortModelChange = (next: GridSortModel): void => {
    setSortModel(next);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  /** Open a record's detail modal, recording the sibling ids it can step through. */
  const openRecord = (record: EsgRecord, ids: Array<string>): void => {
    setSiblingIds(ids);
    setOpenRecordId(record.id);
  };

  /**
   * Shared shell for a multi-record action: busy flag, outcome/problem alerts and a
   * refresh (which also clears the selection). Takes the ids EXPLICITLY so both the
   * selection-driven bulk bar and the sibling-confirm offer — which has no selection
   * and must not disturb one — can run through it.
   */
  const runRecordsAction = async (
    ids: Array<string>,
    action: (ids: Array<string>) => Promise<BulkOutcome>
  ): Promise<void> => {
    setBulkBusy(true);
    setBulkProblem(null);
    setBulkOutcome(null);
    try {
      setBulkOutcome(await action(ids));
      await refreshAll();
    } catch (error: unknown) {
      setBulkProblem(toProblemDetail(error));
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkAction = (action: (ids: Array<string>) => Promise<BulkOutcome>): Promise<void> =>
    runRecordsAction(selection.map(String), action);

  /** Apply one duplicate resolution to the scoped file (the banner's dialog). */
  const handleResolveDuplicates = async (resolution: FileDuplicateResolution): Promise<void> => {
    const file_id = filters.fileId;
    if (file_id === undefined) {
      return;
    }
    setResolvingDuplicates(true);
    setBulkProblem(null);
    try {
      const result = await resolveFileDuplicates(file_id, resolution);
      setDuplicateDialogOpen(false);
      setFeedbackData({ state: true, type: 'success', message: describeResolutionOutcome(result) });
      await refreshAll();
    } catch (error: unknown) {
      setDuplicateDialogOpen(false);
      setBulkProblem(toProblemDetail(error));
    } finally {
      setResolvingDuplicates(false);
    }
  };

  /** Delete the scoped file and every record it produced (reversible via admin). */
  const handleDeleteScopedFile = async (): Promise<void> => {
    const file_id = filters.fileId;
    if (file_id === undefined) {
      return;
    }
    setDeletingFile(true);
    setBulkProblem(null);
    try {
      await deleteFile(file_id);
      setDeleteFileConfirmOpen(false);
      setDuplicateDialogOpen(false);
      setFeedbackData({
        state: true,
        type: 'success',
        message: 'The file and all its records were deleted. An admin can restore them.'
      });
      // The scope's subject is gone — drop the file filter (and the banner's flag
      // filter with it) instead of showing an empty list under a stale chip.
      // handleFiltersChange sees the fileId drop and clears the seed.
      handleFiltersChange({ ...filters, fileId: undefined, flags: undefined });
      setStatsVersion((version) => version + 1);
    } catch (error: unknown) {
      setBulkProblem(toProblemDetail(error));
    } finally {
      setDeletingFile(false);
    }
  };

  const handleBulkApprove = (): Promise<void> =>
    runBulkAction(async (ids) => {
      const outcome = await approveRecordsBulk(ids);
      // Approving suggested mappings also teaches the learned rules (ADR-0002);
      // one toast summarises what the batch taught (silent when nothing).
      const learning = bulkApprovalLearningFeedback(outcome);
      if (learning !== null) {
        setFeedbackData(learning);
      }
      return { verb: 'approved', doneCount: outcome.approved.length, skipped: outcome.skipped };
    });

  const handleBulkDelete = (): Promise<void> =>
    runBulkAction(async (ids) => {
      const outcome = await deleteRecordsBulk(ids);
      return { verb: 'deleted', doneCount: outcome.deleted.length, skipped: outcome.skipped };
    });

  /**
   * Bulk-confirms the AI-SUGGESTED mappings in the selection (ADR-0001 B1): each
   * confirmation re-submits the record's own metric (promoting it to human-verified
   * and teaching the per-client alias). Non-suggested records are reported as
   * skipped, not failed. Sequential on purpose: bounded load, per-record outcomes.
   */
  const handleBulkConfirmMappings = (): Promise<void> =>
    runBulkAction(async (ids) => {
      const skipped: Array<BulkSkippedRecord> = [];
      let doneCount = 0;
      let taughtCount = 0;
      let conflictCount = 0;
      // Rows on the current page already carry mappingStatus/metricId — only
      // off-page selections (kept across pagination) need the extra lookup, so
      // the common case is N requests, not 2N.
      const onPage = new Map((result?.items ?? []).map((item) => [item.id, item]));
      await ids.reduce(
        (previous, recordId) =>
          previous.then(async () => {
            try {
              const record = onPage.get(recordId) ?? (await fetchRecord(recordId));
              if (record.mappingStatus !== 'SUGGESTED' || record.metricId === null) {
                skipped.push({
                  id: recordId,
                  reason: 'not an AI-suggested mapping',
                  rawLabel: record.rawLabel
                });
                return;
              }
              const updated = await updateRecord(recordId, { metricId: record.metricId });
              doneCount += 1;
              if (updated.aliasOutcome === 'CREATED') {
                taughtCount += 1;
              }
              if (updated.aliasOutcome === 'CONFLICT') {
                conflictCount += 1;
              }
            } catch (error: unknown) {
              skipped.push({
                id: recordId,
                reason: toProblemDetail(error).detail ?? 'confirming failed',
                rawLabel: await skipLabel(recordId, onPage)
              });
            }
          }),
        Promise.resolve()
      );
      // One toast for the whole batch: the learning summary, not per-record noise.
      if (doneCount > 0) {
        setFeedbackData(
          conflictCount > 0
            ? {
                state: true,
                type: 'warning',
                message: `Confirmed ${doneCount} mapping${doneCount === 1 ? '' : 's'} — ${taughtCount} label${taughtCount === 1 ? '' : 's'} taught for this client; ${conflictCount} label${conflictCount === 1 ? ' is' : 's are'} contested and will always need review.`
              }
            : {
                state: true,
                type: 'success',
                message: `Confirmed ${doneCount} mapping${doneCount === 1 ? '' : 's'} — ${taughtCount} label${taughtCount === 1 ? '' : 's'} will now map automatically for this client.`
              }
        );
      }
      return { verb: 'confirmed', doneCount, skipped };
    });

  /**
   * Apply one patch to every selected record (Maxine R5 ②): the same PATCH a manual
   * edit sends, once per record, so audit events and alias learning behave exactly
   * like N individual edits. Sequential on purpose (bounded load, per-record
   * outcomes); a failing row is skipped with its reason, never aborting the rest.
   */
  const handleBulkEdit = (patch: UpdateRecordPatch): Promise<void> =>
    runBulkAction(async (ids) => {
      const skipped: Array<BulkSkippedRecord> = [];
      let doneCount = 0;
      let taughtCount = 0;
      let conflictCount = 0;
      const onPage = new Map((result?.items ?? []).map((item) => [item.id, item]));
      await ids.reduce(
        (previous, recordId) =>
          previous.then(async () => {
            try {
              const updated = await updateRecord(recordId, patch);
              doneCount += 1;
              if (updated.aliasOutcome === 'CREATED') {
                taughtCount += 1;
              }
              if (updated.aliasOutcome === 'CONFLICT') {
                conflictCount += 1;
              }
            } catch (error: unknown) {
              skipped.push({
                id: recordId,
                reason: toProblemDetail(error).detail ?? 'editing failed',
                rawLabel: await skipLabel(recordId, onPage)
              });
            }
          }),
        Promise.resolve()
      );
      // A metric change teaches per-label rules like any other edit — one toast for
      // the whole batch (silent when the patch carried no metric or taught nothing).
      if (patch.metricId !== undefined && doneCount > 0 && taughtCount + conflictCount > 0) {
        setFeedbackData(
          conflictCount > 0
            ? {
                state: true,
                type: 'warning',
                message: `${taughtCount} label${taughtCount === 1 ? '' : 's'} taught for this client; ${conflictCount} label${conflictCount === 1 ? ' is' : 's are'} contested and will always need review.`
              }
            : {
                state: true,
                type: 'success',
                message: `${taughtCount} label${taughtCount === 1 ? '' : 's'} will now map automatically for this client.`
              }
        );
      }
      return { verb: 'edited', doneCount, skipped };
    });

  /**
   * After a single inline confirm, look for the other rows in that file carrying the
   * same label which can take the same metric, and offer them as one action. The
   * lookup is best-effort (returns null on failure) — the confirmation itself has
   * already succeeded and must not be reported as failed because the OFFER failed.
   */
  const handleMappingConfirmed = async (updated: EsgRecordUpdateResponse): Promise<void> => {
    setSiblingTarget(await findSiblingsToConfirm(updated));
  };

  /**
   * Apply the confirmed metric to every row in the pending offer. Each PATCH re-enters
   * the alias loop as ALREADY_ACTIVE (same client, label and metric), so this cannot
   * contest the very label it came from — and the originating confirm already told the
   * reviewer what was learned, so this action stays quiet and reports only its counts.
   */
  const handleApplySiblings = (target: SiblingConfirmTarget): Promise<void> =>
    runRecordsAction(target.ids, async (ids) => {
      const skipped: Array<BulkSkippedRecord> = [];
      let doneCount = 0;
      const onPage = new Map((result?.items ?? []).map((item) => [item.id, item]));
      // Sequential, per row: a failure (e.g. a row approved since the offer was made,
      // which 409s) must skip that row with a reason, not abort the rest.
      await ids.reduce(
        (previous, recordId) =>
          previous.then(async () => {
            try {
              await updateRecord(recordId, { metricId: target.metricId });
              doneCount += 1;
            } catch (error: unknown) {
              skipped.push({
                id: recordId,
                reason: toProblemDetail(error).detail ?? 'confirming failed',
                rawLabel: await skipLabel(recordId, onPage)
              });
            }
          }),
        Promise.resolve()
      );
      setSiblingTarget(null);
      return { verb: 'confirmed', doneCount, skipped };
    });

  /**
   * Approve every record in a group in one action: collect the group's ids (the
   * group-scoped list query) and bulk-approve them. The server skips ineligible
   * records (already-approved / unmapped), reported in the outcome summary.
   */
  const handleApproveGroup = async (groupQuery: RecordListQuery): Promise<void> => {
    setBulkBusy(true);
    setBulkProblem(null);
    setBulkOutcome(null);
    try {
      const ids = await fetchAllRecordIds(groupQuery);
      const outcome = await approveRecordsBulk(ids);
      // Group approval teaches like any other approval (ADR-0002) — same summary.
      const learning = bulkApprovalLearningFeedback(outcome);
      if (learning !== null) {
        setFeedbackData(learning);
      }
      setBulkOutcome({
        verb: 'approved',
        doneCount: outcome.approved.length,
        skipped: outcome.skipped
      });
      await refreshAll();
    } catch (error: unknown) {
      setBulkProblem(toProblemDetail(error));
    } finally {
      setBulkBusy(false);
    }
  };

  // The single message for the notification slot, most urgent first: a failed LOAD
  // means there is no data to act on at all, so it outranks a failed action, which
  // in turn outranks a completed one. Each is cleared by its own action, so the next
  // one down surfaces once the one above is resolved or dismissed.
  const notification = ((): {
    severity: 'error' | 'warning' | 'success';
    message: ReactNode;
    detail?: ReactNode;
    onClose?: () => void;
  } | null => {
    if (problem !== null) {
      return {
        severity: 'error',
        message: problem.detail ?? problem.title ?? 'Could not load records.'
      };
    }
    if (bulkProblem !== null) {
      return {
        severity: 'error',
        message: bulkProblem.detail ?? bulkProblem.title ?? 'Bulk action failed.',
        onClose: () => setBulkProblem(null)
      };
    }
    if (bulkOutcome !== null) {
      const { doneCount, verb, skipped } = bulkOutcome;
      const done = `${doneCount} record${doneCount === 1 ? '' : 's'} ${verb}`;
      if (skipped.length === 0) {
        return { severity: 'success', message: done, onClose: () => setBulkOutcome(null) };
      }
      // Nothing went wrong when the only "skips" are records that were already in
      // the state the action wanted — say so plainly instead of raising a warning
      // over a list of ids (Maxine 2026-08-20: "What should be here? This is just
      // a code?").
      const benign = skipped.every((record) => record.reason === SKIP_REASON_ALREADY_APPROVED);
      return {
        severity: benign ? 'success' : 'warning',
        message: benign
          ? `${done}. ${describeAlreadyApproved(skipped.length)}`
          : `${done}, ${skipped.length} skipped:`,
        detail: benign ? undefined : (
          <Stack spacing={0.25}>
            {skipped.map((skippedRecord) => (
              <CaptionText key={skippedRecord.id}>
                {nameSkippedRecord(skippedRecord, recordLabels)}: {skippedRecord.reason}
              </CaptionText>
            ))}
          </Stack>
        ),
        onClose: () => setBulkOutcome(null)
      };
    }
    return null;
  })();

  return (
    <Box sx={{ py: 3 }}>
      <Stack spacing={2}>
        {/* COMMON REGION: progress, scope, triage counts and the filter controls are
            one bordered block, separated from the data below. The panel used to
            stack up to eleven full-width bands at identical weight, which gave the
            eye no entry point and pushed the grid below the fold; now the screen
            reads summary → filters → data. */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {completeness !== null && completeness.total > 0 && (
              // The completeness score leads the panel: approved records fill the bar
              // toward 100%, so the reviewer sees how much of the workbook is done.
              <CompletenessMeter approved={completeness.approved} total={completeness.total} />
            )}
            {filters.fileId !== undefined && (
              // The active file filter must be visible and dismissible — an invisible
              // filter reads as "my other records disappeared". It sits with the
              // progress it scopes, and carries the learning payoff rather than
              // spending a second full-width band on it.
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Showing records from: ${
                    seed !== null && seed.fileId === filters.fileId
                      ? seed.filename
                      : 'uploaded file'
                  }`}
                  onDelete={() => {
                    // handleFiltersChange sees the fileId drop and clears the seed.
                    handleFiltersChange({ ...filters, fileId: undefined, flags: undefined });
                  }}
                />
                {completeness !== null && completeness.learned > 0 && (
                  // The learning loop's payoff, at the moment it pays off: these
                  // records arrived already mapped because of the team's own past
                  // confirmations (LEARNING_LOOP.md, #43).
                  <CaptionText>
                    {completeness.learned} of {completeness.total} record
                    {completeness.total === 1 ? '' : 's'} mapped instantly from rules your team
                    taught earlier.
                  </CaptionText>
                )}
                {!readOnly && (
                  // The retroactive escape hatch (Maxine 2026-08-19): undo this
                  // whole upload — records and all — from where it is being reviewed.
                  <Chip
                    size="small"
                    variant="outlined"
                    color="error"
                    label="Delete file and records…"
                    disabled={deletingFile || completeness === null}
                    onClick={() => {
                      setDeleteConfirmRecordCount(completeness?.allRecordsCount ?? null);
                      setDeleteFileConfirmOpen(true);
                    }}
                  />
                )}
              </Stack>
            )}
            {!readOnly &&
              filters.fileId !== undefined &&
              duplicateSummary !== null &&
              duplicateSummary.resolution === null &&
              duplicateSummary.duplicateCount + duplicateSummary.conflictCount > 0 && (
                <FileDuplicateBanner
                  fileName={
                    seed !== null && seed.fileId === filters.fileId ? seed.filename : 'this file'
                  }
                  summary={duplicateSummary}
                  onOpenDialog={() => setDuplicateDialogOpen(true)}
                  onViewAffected={() =>
                    handleFiltersChange({
                      ...filters,
                      flags: 'CROSS_FILE_DUPLICATE,CROSS_FILE_CONFLICT'
                    })
                  }
                />
              )}
            <Divider flexItem />
            <ReviewScopeTabs scope={scopeFromFilters(filters)} onScopeChange={handleScopeChange} />
            <ReviewSummaryChips
              stats={stats}
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
            <ReviewFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
              onRefresh={() => void refreshAll()}
              loading={loading}
            />
          </Stack>
        </Paper>
        {/* ONE notification slot. Four alerts could previously stack independently,
            each pushing the grid down and springing it back on dismiss; the reviewer
            only ever acts on the most recent one. */}
        {notification !== null && (
          <Alert severity={notification.severity} onClose={notification.onClose}>
            {notification.message}
            {notification.detail}
          </Alert>
        )}
        {!readOnly && selection.length > 0 && (
          <BulkActionBar
            selectedCount={selection.length}
            busy={bulkBusy}
            onApproveSelected={handleBulkApprove}
            onDeleteSelected={handleBulkDelete}
            onConfirmMappingsSelected={handleBulkConfirmMappings}
            onEditSelected={() => setBulkEditOpen(true)}
            onClearSelection={() => setSelection([])}
          />
        )}
        {!readOnly && (
          <BulkEditDialog
            open={bulkEditOpen}
            recordIds={selection.map(String)}
            busy={bulkBusy}
            onApply={handleBulkEdit}
            onClose={() => setBulkEditOpen(false)}
          />
        )}
        {!readOnly && duplicateSummary !== null && (
          <FileDuplicateChoiceDialog
            open={duplicateDialogOpen}
            fileName={seed !== null && seed.fileId === filters.fileId ? seed.filename : 'this file'}
            summary={duplicateSummary}
            busy={resolvingDuplicates || deletingFile}
            onResolve={handleResolveDuplicates}
            onDeleteFile={handleDeleteScopedFile}
            onClose={() => setDuplicateDialogOpen(false)}
          />
        )}
        {!readOnly && (
          <ConfirmationModal
            open={deleteFileConfirmOpen}
            title="Delete this file and its records?"
            description={`This deletes the file and all ${deleteConfirmRecordCount ?? ''} record${deleteConfirmRecordCount === 1 ? '' : 's'} extracted from it, including any already approved. An admin can restore it later.`}
            confirm_button_text="Delete file and records"
            confirm_loading={deletingFile}
            handleCloseModal={() => setDeleteFileConfirmOpen(false)}
            handleConfirm={handleDeleteScopedFile}
          />
        )}
        {groupBy === 'none' ? (
          <RecordsGrid
            records={result?.items ?? []}
            total={result?.total ?? 0}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            selection={selection}
            onSelectionChange={setSelection}
            onRowOpen={(record: EsgRecord) =>
              openRecord(
                record,
                (result?.items ?? []).map((item) => item.id)
              )
            }
            onRecordMutated={() => void refreshAll()}
            onMappingConfirmed={(updated) => void handleMappingConfirmed(updated)}
            viewedFileId={filters.fileId}
            readOnly={readOnly}
          />
        ) : (
          <RecordGroupList
            groupBy={groupBy}
            filters={filters}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            selection={selection}
            onSelectionChange={setSelection}
            onOpenRecord={openRecord}
            onMappingConfirmed={(updated) => void handleMappingConfirmed(updated)}
            onApproveGroup={handleApproveGroup}
            busy={bulkBusy}
            refreshKey={statsVersion}
            readOnly={readOnly}
          />
        )}
      </Stack>
      {/* Docks itself to the bottom of the viewport (portalled), so it contributes
          nothing to this panel's layout. Placed here beside the modal — the other
          overlay — rather than in the header stack, where its JSX position would
          misdescribe where it actually appears. */}
      {!readOnly && siblingTarget !== null && (
        <SiblingConfirmBar
          target={siblingTarget}
          busy={bulkBusy}
          onApply={() => void handleApplySiblings(siblingTarget)}
          onDismiss={() => setSiblingTarget(null)}
        />
      )}
      <RecordDetailModal
        readOnly={readOnly}
        recordId={openRecordId}
        onClose={() => setOpenRecordId(null)}
        onRecordMutated={() => void refreshAll()}
        recordIds={siblingIds}
        onNavigate={(id) => setOpenRecordId(id)}
      />
    </Box>
  );
}
